import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Collapse,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Space,
  Tag,
  Spin,
  Popconfirm,
  Drawer,
  Statistic,
  Row,
  Col,
  Progress,
  Card,
  Divider,
  Empty,
  Alert,
  Tooltip,
  DatePicker,
} from "antd";
import dayjs from "dayjs";
import {
  LoadingOutlined,
  SearchOutlined,
  FilterOutlined,
  PlusOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  FileTextOutlined,
  BarChartOutlined,
  DeleteOutlined,
  EditOutlined,
  ReloadOutlined,
  SearchOutlined as SearchIcon,
  CopyOutlined as CopyIcon,
} from "@ant-design/icons";
import useCampaignData from "../hooks/useCampaignData";
import SimpleEditor from "./SimpleEditor";
import { _get_campaign_logs } from "../../../common/axios_services";

const recipientTypes = ["DRIVER", "BUYER", "BUSINESS", "RESTAURANT", "AMBASSADOR", "ALL"];
const campaignTypes = ["BROADCAST", "TARGETED", "SCHEDULED_REPORT"];
const channels = ["PUSH", "EMAIL", "SMS", "WHATSAPP"];
const statuses = [
  "DRAFT",
  "SCHEDULED",
  "PROCESSING",
  "COMPLETED",
  "FAILED",
  "PAUSED",
];

const AVAILABLE_VARIABLES = [
  { name: "firstName", example: "John", description: "User first name" },
  { name: "lastName", example: "Doe", description: "User last name" },
  { name: "fullName", example: "John Doe", description: "User full name" },
  { name: "email", example: "john@example.com", description: "User email" },
  {
    name: "phoneNumber",
    example: "+2348012345678",
    description: "User phone number",
  },
  { name: "userId", example: "user-123abc", description: "User ID" },
  { name: "accountId", example: "user-123abc", description: "Account ID" },
  {
    name: "magicLink",
    example: "https://konnect.ws/verify-sms-link?token=...",
    description: "1-Click SMS magic link to verify user phone & log in",
  },
];

const getStatusColor = (status) => {
  const colors = {
    DRAFT: "default",
    SCHEDULED: "processing",
    PROCESSING: "processing",
    COMPLETED: "success",
    FAILED: "error",
    PAUSED: "warning",
  };
  return colors[status] || "default";
};

const AdminCampaigns = ({ type = "ONE_OFF" }) => {
  const {
    campaigns,
    stats,
    report,
    templatePreview,
    loading,
    fetchCampaigns,
    fetchStats,
    fetchReport,
    createCampaign,
    updateCampaign,
    startCampaign,
    pauseCampaign,
    resumeCampaign,
    deleteCampaign,
    previewTemplate,
    resendCampaign,
    fetchRecipientCounts,
  } = useCampaignData();

  const userProfile = useSelector((state) => state.userProfile?.user);
  const userRole = userProfile?.role?.toUpperCase() || "";

  const [form] = Form.useForm();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [reportDrawerOpen, setReportDrawerOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [variablesModalOpen, setVariablesModalOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filterStatus, setFilterStatus] = useState(null);
  const [filterOnboardingStage, setFilterOnboardingStage] = useState(null);
  const [filterRecipientType, setFilterRecipientType] = useState(null);
  const [filterChannel, setFilterChannel] = useState(null);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [sampleData, setSampleData] = useState({});
  const [validationErrors, setValidationErrors] = useState([]);
  const [recipientCounts, setRecipientCounts] = useState({
    byRecipientType: { ALL: 0, BUYER: 0, DRIVER: 0, BUSINESS: 0, RESTAURANT: 0, AMBASSADOR: 0 },
    bySmsVerification: { ALL: 0, VERIFIED: 0, UNVERIFIED: 0 },
    byOnboardingStage: { ALL: 0, PRE_ONBOARD: 0, POST_ONBOARD: 0 },
  });

  const watchedRecipientType = Form.useWatch("recipientType", form);
  const watchedSmsVerification = Form.useWatch("smsVerificationFilter", form);
  const watchedOnboardingStage = Form.useWatch("onboardingStageFilter", form);
  const watchedChannels = Form.useWatch("channels", form);

  const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

  useEffect(() => {
    fetchCampaigns({ type });
  }, [type, fetchCampaigns]);

  useEffect(() => {
    fetchRecipientCounts({
      recipientType: watchedRecipientType || filterRecipientType || "ALL",
      smsVerificationFilter: watchedSmsVerification || "ALL",
      onboardingStageFilter: watchedOnboardingStage || filterOnboardingStage || "ALL",
      channel: watchedChannels || filterChannel || undefined,
    }).then((res) => {
      if (res?.success && res?.data) {
        setRecipientCounts(res.data);
      }
    });
  }, [
    modalOpen,
    filterModalOpen,
    watchedRecipientType,
    watchedSmsVerification,
    watchedOnboardingStage,
    watchedChannels,
    filterRecipientType,
    filterOnboardingStage,
    filterChannel,
    fetchRecipientCounts,
  ]);

  const filteredCampaigns = useCallback(() => {
    return campaigns.filter((c) => {
      const q = searchText.trim().toLowerCase();
      const matchesSearch =
        !q ||
        (c.name && c.name.toLowerCase().includes(q)) ||
        (c.title && c.title.toLowerCase().includes(q)) ||
        (c.description && c.description.toLowerCase().includes(q));
      const matchesStatus = !filterStatus || c.status === filterStatus;
      const matchesStage =
        !filterOnboardingStage ||
        c.filters?.onboardingStage === filterOnboardingStage;
      const matchesRecipient =
        !filterRecipientType || c.recipientType === filterRecipientType;
      const matchesChannel =
        !filterChannel || (c.channels && c.channels.includes(filterChannel));
      return (
        matchesSearch &&
        matchesStatus &&
        matchesStage &&
        matchesRecipient &&
        matchesChannel
      );
    });
  }, [
    campaigns,
    searchText,
    filterStatus,
    filterOnboardingStage,
    filterRecipientType,
    filterChannel,
  ]);

  const activeFiltersCount =
    (filterStatus ? 1 : 0) +
    (filterOnboardingStage ? 1 : 0) +
    (filterRecipientType ? 1 : 0) +
    (filterChannel ? 1 : 0);

  const handleCreate = () => {
    form.resetFields();
    setEditingId(null);
    setModalOpen(true);
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    form.setFieldsValue({
      name: record.name,
      description: record.description,
      recipientType: record.recipientType,
      campaignType: record.campaignType,
      channels: record.channels,
      title: record.title,
      body: record.body,
      batchSize: record.batchSize || 10,
      batchPauseMs: record.batchPauseMs || 60000,
      scheduledFor: record.scheduledFor ? dayjs(record.scheduledFor) : null,
      scheduledTime: record.scheduledTime,
      scheduledDays: record.scheduledDays
        ? record.scheduledDays.split(",")
        : [],
      smsVerificationFilter:
        record.filters?.smsVerified !== undefined
          ? record.filters.smsVerified
            ? "VERIFIED"
            : "UNVERIFIED"
          : "ALL",
      onboardingStageFilter: record.filters?.onboardingStage || "ALL",
    });
    setModalOpen(true);
  };

  const handleSave = async (values) => {
    try {
      const payload = {
        ...values,
        type,
        campaignType:
          type === "ONE_OFF" ? "BROADCAST" : values.campaignType || "TARGETED",
        scheduledFor: values.scheduledFor
          ? values.scheduledFor.toISOString()
          : null,
        scheduledDays: values.scheduledDays
          ? values.scheduledDays.join(",")
          : null,
        filters: {
          ...(values.filters || {}),
          ...(values.smsVerificationFilter && values.smsVerificationFilter !== "ALL"
            ? { smsVerified: values.smsVerificationFilter === "VERIFIED" }
            : {}),
          ...(values.onboardingStageFilter && values.onboardingStageFilter !== "ALL"
            ? { onboardingStage: values.onboardingStageFilter }
            : {}),
        },
      };

      if (editingId) {
        const result = await updateCampaign(editingId, payload);
        if (result.success) {
          message.success("Campaign updated successfully");
          setModalOpen(false);
          form.resetFields();
        } else {
          message.error("Failed to update campaign");
        }
      } else {
        const result = await createCampaign(payload);
        if (result.success) {
          message.success("Campaign created successfully");
          setModalOpen(false);
          form.resetFields();
        } else {
          message.error("Failed to create campaign");
        }
      }
    } catch (error) {
      message.error(
        error?.response?.data?.message ||
          error?.message ||
          "Error saving campaign",
      );
    }
  };

  const handleStart = async (id) => {
    const result = await startCampaign(id);
    if (result.success) {
      message.success("Campaign started");
    } else {
      message.error("Failed to start campaign");
    }
  };

  const handlePause = async (id) => {
    const result = await pauseCampaign(id);
    if (result.success) {
      message.success("Campaign paused");
    } else {
      message.error("Failed to pause campaign");
    }
  };

  const handleResume = async (id) => {
    const result = await resumeCampaign(id);
    if (result.success) {
      message.success("Campaign resumed");
    } else {
      message.error("Failed to resume campaign");
    }
  };

  const handleResend = async (id) => {
    const result = await resendCampaign(id);
    if (result.success) {
      message.success("Campaign resending to all recipients");
    } else {
      message.error("Failed to resend campaign");
    }
  };

  const handleDelete = async (id) => {
    const result = await deleteCampaign(id);
    if (result.success) {
      message.success("Campaign deleted");
    } else {
      message.error("Failed to delete campaign");
    }
  };

  const [campaignLogs, setCampaignLogs] = useState([]);

  const handleViewReport = async (id) => {
    await fetchReport(id);
    try {
      const res = await _get_campaign_logs(id);
      setCampaignLogs(res?.data?.data || res?.data || []);
    } catch (e) {
      setCampaignLogs([]);
    }
    setReportDrawerOpen(true);
  };

  const extractVariables = (text) => {
    const regex = /\{\{(\w+)\}\}/g;
    const matches = [...text.matchAll(regex)];
    return new Set(matches.map((m) => m[1]));
  };

  const validateTemplate = () => {
    const errors = [];
    const bodyVars = extractVariables(form.getFieldValue("body") || "");
    const titleVars = extractVariables(form.getFieldValue("title") || "");
    const allVars = new Set([...bodyVars, ...titleVars]);
    const availableVarNames = AVAILABLE_VARIABLES.map((v) => v.name);

    allVars.forEach((varName) => {
      if (!availableVarNames.includes(varName)) {
        errors.push(`Invalid variable: {{${varName}}} not available`);
      }
    });

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const getRequiredVariables = () => {
    const bodyVars = extractVariables(form.getFieldValue("body") || "");
    const titleVars = extractVariables(form.getFieldValue("title") || "");
    return new Set([...bodyVars, ...titleVars]);
  };

  const handlePreview = async () => {
    if (!validateTemplate()) {
      message.error("Please fix template errors");
      return;
    }

    const requiredVars = getRequiredVariables();
    const missing = [...requiredVars].filter((v) => !sampleData[v]);
    if (missing.length > 0) {
      message.warning(`Missing values: ${missing.join(", ")}`);
      return;
    }

    const dataToSend = {};
    requiredVars.forEach((v) => {
      dataToSend[v] = sampleData[v];
    });

    let campaignId = editingId;
    if (!campaignId) {
      const formData = form.getFieldsValue();
      const createResult = await createCampaign({
        ...formData,
        body: form.getFieldValue("body") || "",
      });
      if (createResult.success) {
        campaignId = createResult.data?.id;
      } else {
        message.error("Failed to save campaign for preview");
        return;
      }
    }

    await previewTemplate(campaignId, dataToSend);
  };

  const copyVariable = (varName) => {
    navigator.clipboard.writeText(`{{${varName}}}`);
    message.success(`Copied {{${varName}}}`);
  };

  const renderCollapseItems = () => {
    return filteredCampaigns().map((campaign) => ({
      key: campaign.id,
      label: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <div>
            <p style={{ display: "inline", margin: 0, marginRight: "16px" }}>
              <strong>{campaign.name}</strong>
            </p>
            <Tag color={getStatusColor(campaign.status)}>{campaign.status}</Tag>
          </div>
          <span style={{ fontSize: "12px", color: "#999" }}>
            {campaign.totalRecipients?.toLocaleString()} recipients •{" "}
            {campaign.sentCount?.toLocaleString()} sent
          </span>
        </div>
      ),
      children: (
        <div className="space-y-4">
          <Row gutter={16}>
            <Col span={12}>
              <p className="">
                <strong>Type:</strong> {campaign.recipientType}
              </p>
            </Col>
            <Col span={12}>
              <p className="">
                <strong>
                  {type === "RECURRING" ? "Campaign" : "Broadcast"} Type:
                </strong>{" "}
                {campaign.campaignType}
              </p>
            </Col>
          </Row>

          {type === "RECURRING" && (
            <Row gutter={16}>
              <Col span={12}>
                <p className="">
                  <strong>Recurrence:</strong>{" "}
                  {campaign.scheduledTime ? "CUSTOM" : campaign.recurrence}
                </p>
              </Col>
              <Col span={12}>
                <p className="">
                  <strong>Schedule:</strong> {campaign.scheduledTime} (
                  {campaign.scheduledDays})
                </p>
              </Col>
            </Row>
          )}

          <Row gutter={16}>
            <Col span={24}>
              <p className="">
                <strong>Channels:</strong>
              </p>
              <div className="mt-1">
                {campaign.channels?.map((ch) => (
                  <Tag key={ch} color="cyan" className="mr-2">
                    {ch}
                  </Tag>
                ))}
              </div>
            </Col>
          </Row>

          {(campaign.filters?.onboardingStage || campaign.filters?.smsVerified !== undefined) && (
            <Row gutter={16}>
              {campaign.filters?.onboardingStage && (
                <Col span={12}>
                  <p className="">
                    <strong>Onboarding Stage:</strong>{" "}
                    <Tag color={campaign.filters.onboardingStage === "PRE_ONBOARD" ? "blue" : "purple"}>
                      {campaign.filters.onboardingStage}
                    </Tag>
                  </p>
                </Col>
              )}
              {campaign.filters?.smsVerified !== undefined && (
                <Col span={12}>
                  <p className="">
                    <strong>SMS Filter:</strong>{" "}
                    <Tag color={campaign.filters.smsVerified ? "green" : "orange"}>
                      {campaign.filters.smsVerified ? "Verified Only" : "Unverified Only"}
                    </Tag>
                  </p>
                </Col>
              )}
            </Row>
          )}

          {campaign.description && (
            <Row gutter={16}>
              <Col span={24}>
                <p className="">
                  <strong>Description:</strong> {campaign.description}
                </p>
              </Col>
            </Row>
          )}

          <Row gutter={16}>
            <Col span={12}>
              <p className="">
                <strong>Title:</strong> {campaign.title}
              </p>
            </Col>
            <Col span={12}>
              <p className="">
                <strong>Total Recipients:</strong>{" "}
                {campaign.totalRecipients?.toLocaleString()}
              </p>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <p className="">
                <strong>Sent:</strong> {campaign.sentCount?.toLocaleString()}
              </p>
            </Col>
            <Col span={12}>
              <p className="">
                <strong>Failed:</strong>{" "}
                <span className="text-red-600">
                  {campaign.failedCount?.toLocaleString()}
                </span>
              </p>
            </Col>
          </Row>

          {(campaign.startedAt || campaign.completedAt) && (
            <Row gutter={16}>
              <Col span={12}>
                {campaign.startedAt && (
                  <p className="text-gray-500 text-xs">
                    <strong>Started:</strong>{" "}
                    {dayjs(campaign.startedAt).format("MMM D, YYYY HH:mm")}
                  </p>
                )}
              </Col>
              <Col span={12}>
                {campaign.completedAt && (
                  <p className="text-gray-500 text-xs">
                    <strong>Completed:</strong>{" "}
                    {dayjs(campaign.completedAt).format("MMM D, YYYY HH:mm")}
                  </p>
                )}
              </Col>
            </Row>
          )}

          <Divider style={{ margin: "16px 0" }} />

          <Space size="small" wrap>
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(campaign)}
            >
              Edit
            </Button>
            {campaign.status === "DRAFT" && (
              <Button
                type="default"
                size="small"
                icon={<PlayCircleOutlined />}
                onClick={() => handleStart(campaign.id)}
              >
                Start
              </Button>
            )}
            {campaign.status === "PROCESSING" && (
              <Button
                type="default"
                size="small"
                icon={<PauseCircleOutlined />}
                onClick={() => handlePause(campaign.id)}
              >
                Pause
              </Button>
            )}
            {campaign.status === "PAUSED" && (
              <Button
                type="default"
                size="small"
                icon={<PlayCircleOutlined />}
                onClick={() => handleResume(campaign.id)}
              >
                Resume
              </Button>
            )}
            {(campaign.sentCount > 0 ||
              campaign.status === "COMPLETED" ||
              campaign.status === "PROCESSING" ||
              campaign.status === "FAILED") && (
              <Button
                type="default"
                size="small"
                icon={<FileTextOutlined />}
                onClick={() => handleViewReport(campaign.id)}
              >
                Report
              </Button>
            )}
            {(campaign.status === "COMPLETED" ||
              campaign.status === "FAILED") && (
              <Popconfirm
                title="Resend Campaign"
                description="This will reset counters and resend to all recipients. Continue?"
                onConfirm={() => handleResend(campaign.id)}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  type="default"
                  size="small"
                  icon={<PlayCircleOutlined />}
                >
                  Resend
                </Button>
              </Popconfirm>
            )}
            <Popconfirm
              title={`Delete ${type === "RECURRING" ? "Campaign" : "Broadcast"}`}
              description="Are you sure?"
              onConfirm={() => handleDelete(campaign.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="primary"
                danger
                size="small"
                icon={<DeleteOutlined />}
              >
                Delete
              </Button>
            </Popconfirm>
          </Space>
        </div>
      ),
    }));
  };

  return (
    <Spin spinning={loading} indicator={antIcon}>
      <div className="pb-10">
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
            <h3 className="text-lg font-bold mb-0">
              {type === "RECURRING" ? "Recurring Campaigns" : "Broadcasts"}
            </h3>
            <Button
              type="primary"
              size="large"
              icon={<PlusOutlined />}
              onClick={handleCreate}
              className="bg-blue-600 hover:bg-blue-700 font-semibold rounded-lg flex items-center justify-center gap-1 shadow-sm"
            >
              Create New {type === "RECURRING" ? "Campaign" : "Broadcast"}
            </Button>
          </div>

          <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm mb-4 flex items-center gap-2">
            <Input
              prefix={<SearchOutlined className="text-gray-400" />}
              placeholder={
                type === "RECURRING"
                  ? "Search campaigns..."
                  : "Search broadcasts..."
              }
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              className="rounded-lg h-10 border-gray-200 text-sm flex-1"
            />
            <Tooltip title="Filter options">
              <Button
                size="large"
                className={`h-10 px-3.5 rounded-lg flex items-center justify-center gap-2 border-gray-200 ${
                  activeFiltersCount > 0
                    ? "border-blue-500 text-blue-600 bg-blue-50 font-bold"
                    : "hover:border-gray-400 text-gray-600"
                }`}
                onClick={() => setFilterModalOpen(true)}
              >
                <FilterOutlined style={{ fontSize: "16px" }} />
                {activeFiltersCount > 0 && (
                  <span className="bg-blue-600 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
                    {activeFiltersCount}
                  </span>
                )}
              </Button>
            </Tooltip>
            <Tooltip title="Refresh list">
              <Button
                size="large"
                icon={<ReloadOutlined />}
                onClick={() => fetchCampaigns({ type })}
                className="h-10 w-10 rounded-lg border-gray-200 flex items-center justify-center text-gray-600"
              />
            </Tooltip>
          </div>

          {activeFiltersCount > 0 && (
            <div className="flex items-center gap-2 mb-4 flex-wrap bg-gray-50 p-3 rounded-lg border border-gray-200">
              <span className="text-xs font-semibold text-gray-500">Active Filters:</span>
              {filterStatus && (
                <Tag
                  closable
                  onClose={() => setFilterStatus(null)}
                  color="blue"
                  className="rounded-md px-2 py-0.5"
                >
                  Status: {filterStatus}
                </Tag>
              )}
              {filterOnboardingStage && (
                <Tag
                  closable
                  onClose={() => setFilterOnboardingStage(null)}
                  color="purple"
                  className="rounded-md px-2 py-0.5"
                >
                  Stage: {filterOnboardingStage === "PRE_ONBOARD" ? "Pre-onboard" : "Post-onboard"}
                </Tag>
              )}
              {filterRecipientType && (
                <Tag
                  closable
                  onClose={() => setFilterRecipientType(null)}
                  color="cyan"
                  className="rounded-md px-2 py-0.5"
                >
                  Recipient: {filterRecipientType}
                </Tag>
              )}
              {filterChannel && (
                <Tag
                  closable
                  onClose={() => setFilterChannel(null)}
                  color="orange"
                  className="rounded-md px-2 py-0.5"
                >
                  Channel: {filterChannel}
                </Tag>
              )}
              <Button
                type="link"
                size="small"
                onClick={() => {
                  setFilterStatus(null);
                  setFilterOnboardingStage(null);
                  setFilterRecipientType(null);
                  setFilterChannel(null);
                }}
                className="text-xs text-red-500 p-0 ml-1"
              >
                Clear All
              </Button>
            </div>
          )}

          {filteredCampaigns().length > 0 ? (
            <Collapse items={renderCollapseItems()} />
          ) : (
            <Empty description="No campaigns found" />
          )}
        </div>
      </div>

      <Modal
        title={
          <div className="flex items-center gap-2 text-base font-bold">
            <FilterOutlined className="text-blue-600" />
            <span>Filter {type === "RECURRING" ? "Campaigns" : "Broadcasts"}</span>
          </div>
        }
        open={filterModalOpen}
        onCancel={() => setFilterModalOpen(false)}
        footer={[
          <Button
            key="reset"
            onClick={() => {
              setFilterStatus(null);
              setFilterOnboardingStage(null);
              setFilterRecipientType(null);
              setFilterChannel(null);
            }}
          >
            Reset Filters
          </Button>,
          <Button
            key="apply"
            type="primary"
            onClick={() => setFilterModalOpen(false)}
          >
            Apply Filters
          </Button>,
        ]}
        centered
        width={440}
      >
        <div className="space-y-4 py-2">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Status
            </label>
            <Select
              placeholder="All Statuses"
              value={filterStatus}
              onChange={setFilterStatus}
              allowClear
              className="w-full"
              size="large"
            >
              {statuses.map((s) => (
                <Select.Option key={s} value={s}>
                  {s}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Onboarding Stage
            </label>
            <Select
              placeholder="All Stages"
              value={filterOnboardingStage}
              onChange={setFilterOnboardingStage}
              allowClear
              className="w-full"
              size="large"
            >
              <Select.Option value="PRE_ONBOARD">Pre-onboard</Select.Option>
              <Select.Option value="POST_ONBOARD">Post-onboard</Select.Option>
            </Select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Recipient Type
            </label>
            <Select
              placeholder="All Recipient Types"
              value={filterRecipientType}
              onChange={setFilterRecipientType}
              allowClear
              className="w-full"
              size="large"
              options={recipientTypes.map((r) => {
                const count = recipientCounts?.byRecipientType?.[r];
                const countLabel =
                  count !== undefined ? ` (${count.toLocaleString()})` : "";
                return {
                  value: r,
                  label: `${r}${countLabel}`,
                };
              })}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Channel
            </label>
            <Select
              placeholder="All Channels"
              value={filterChannel}
              onChange={setFilterChannel}
              allowClear
              className="w-full"
              size="large"
            >
              {channels.map((ch) => (
                <Select.Option key={ch} value={ch}>
                  {ch}
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>
      </Modal>

      <Modal
        title={
          editingId
            ? `Edit ${type === "RECURRING" ? "Campaign" : "Broadcast"}`
            : `Create ${type === "RECURRING" ? "Campaign" : "Broadcast"}`
        }
        open={modalOpen}
        onOk={() => form.submit()}
        onCancel={() => {
          setModalOpen(false);
          form.resetFields();
        }}
        width="90%"
        style={{ maxWidth: "1000px" }}
        centered
        styles={{ body: { maxHeight: "80vh", overflowY: "auto" } }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setModalOpen(false);
              form.resetFields();
            }}
          >
            Close
          </Button>,
          <Button key="submit" type="primary" onClick={() => form.submit()}>
            {editingId
              ? "Save Changes"
              : `Create ${type === "RECURRING" ? "Campaign" : "Broadcast"}`}
          </Button>,
        ]}
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <div className="flex gap-2 justify-end mb-4 pb-4 border-b border-gray-200">
            <Button onClick={() => setVariablesModalOpen(true)}>
              View Variables
            </Button>
            <Button onClick={() => setPreviewModalOpen(true)}>Preview</Button>
          </div>

          {validationErrors.length > 0 && (
            <Alert
              message="Template Errors"
              description={
                <ul className="space-y-1 mb-0">
                  {validationErrors.map((err, idx) => (
                    <li key={idx}>• {err}</li>
                  ))}
                </ul>
              }
              type="error"
              closable
              className="mb-4"
            />
          )}

          <Form.Item
            name="name"
            label={`${type === "RECURRING" ? "Campaign" : "Broadcast"} Name`}
            rules={[{ required: true, message: "Required" }]}
          >
            <Input placeholder="e.g. Holiday Promo" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="Campaign description" />
          </Form.Item>

          <Form.Item
            name="recipientType"
            label="Recipient Type"
            rules={[{ required: true, message: "Required" }]}
          >
            <Select
              placeholder="Select target audience"
              options={recipientTypes.map((rt) => {
                const count = recipientCounts?.byRecipientType?.[rt];
                const countLabel =
                  count !== undefined ? ` (${count.toLocaleString()})` : "";
                return {
                  value: rt,
                  label: `${rt}${countLabel}`,
                };
              })}
            />
          </Form.Item>

          <Form.Item
            name="smsVerificationFilter"
            label="SMS Verification"
            initialValue="ALL"
            tooltip="Limit this campaign to users who have or have not completed SMS verification"
          >
            <Select
              options={[
                {
                  value: "ALL",
                  label: `All users${
                    recipientCounts?.bySmsVerification?.ALL !== undefined
                      ? ` (${recipientCounts.bySmsVerification.ALL.toLocaleString()})`
                      : ""
                  }`,
                },
                {
                  value: "VERIFIED",
                  label: `SMS verified only${
                    recipientCounts?.bySmsVerification?.VERIFIED !== undefined
                      ? ` (${recipientCounts.bySmsVerification.VERIFIED.toLocaleString()})`
                      : ""
                  }`,
                },
                {
                  value: "UNVERIFIED",
                  label: `SMS unverified only${
                    recipientCounts?.bySmsVerification?.UNVERIFIED !== undefined
                      ? ` (${recipientCounts.bySmsVerification.UNVERIFIED.toLocaleString()})`
                      : ""
                  }`,
                },
              ]}
            />
          </Form.Item>

          <Form.Item
            name="onboardingStageFilter"
            label="Onboarding Stage Filter"
            initialValue="ALL"
            tooltip="Target Pre-onboard (SMS verified users signing up today onwards) or Post-onboard users"
          >
            <Select
              options={[
                {
                  value: "ALL",
                  label: `All Users${
                    recipientCounts?.byOnboardingStage?.ALL !== undefined
                      ? ` (${recipientCounts.byOnboardingStage.ALL.toLocaleString()})`
                      : ""
                  }`,
                },
                {
                  value: "PRE_ONBOARD",
                  label: `Pre-onboard Users (SMS Verified & Signed Up Today+)${
                    recipientCounts?.byOnboardingStage?.PRE_ONBOARD !== undefined
                      ? ` (${recipientCounts.byOnboardingStage.PRE_ONBOARD.toLocaleString()})`
                      : ""
                  }`,
                },
                {
                  value: "POST_ONBOARD",
                  label: `Post-onboard Users (Outside Pre-onboard)${
                    recipientCounts?.byOnboardingStage?.POST_ONBOARD !== undefined
                      ? ` (${recipientCounts.byOnboardingStage.POST_ONBOARD.toLocaleString()})`
                      : ""
                  }`,
                },
              ]}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="batchSize"
                label="Batch Size"
                initialValue={10}
                tooltip="Number of recipients sent concurrently in one batch"
              >
                <Input type="number" min={1} placeholder="e.g. 10" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="batchPauseMs"
                label="Batch Pause (ms)"
                initialValue={60000}
                tooltip="Pause duration between batches in milliseconds (60000ms = 1 minute)"
              >
                <Input type="number" min={0} placeholder="e.g. 60000" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="channels"
            label="Channels"
            rules={[{ required: true, message: "Select at least one channel" }]}
          >
            <Select mode="multiple" placeholder="Select channels">
              {channels.map((ch) => (
                <Select.Option key={ch} value={ch}>
                  {ch}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: "Required" }]}
          >
            <Input placeholder="Message title" />
          </Form.Item>

          <Form.Item
            name="body"
            label="Message Body"
            rules={[{ required: true, message: "Required" }]}
          >
            <SimpleEditor
              value={form.getFieldValue("body") || ""}
              onChange={(html) => form.setFieldValue("body", html)}
              placeholder="Message content"
            />
          </Form.Item>

          <Form.Item
            name="scheduledFor"
            label={
              type === "RECURRING" ? "Start Date" : "Schedule For (Optional)"
            }
            tooltip="Set a date and time to automatically start this campaign. Leave empty to start manually."
          >
            <DatePicker
              showTime
              style={{ width: "100%" }}
              placeholder="Select date and time"
              disabledDate={(current) =>
                current && current < dayjs().startOf("day")
              }
            />
          </Form.Item>

          {type === "RECURRING" && (
            <>
              <Form.Item
                name="scheduledTime"
                label="Scheduled Time (HH:mm)"
                rules={[{ required: true, message: "Required" }]}
                initialValue="09:00"
              >
                <Input type="time" placeholder="09:00" />
              </Form.Item>

              <Form.Item
                name="scheduledDays"
                label="Scheduled Days"
                rules={[{ required: true, message: "Required" }]}
                initialValue={["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"]}
              >
                <Select mode="multiple" placeholder="Select days">
                  <Select.Option value="MON">Monday</Select.Option>
                  <Select.Option value="TUE">Tuesday</Select.Option>
                  <Select.Option value="WED">Wednesday</Select.Option>
                  <Select.Option value="THU">Thursday</Select.Option>
                  <Select.Option value="FRI">Friday</Select.Option>
                  <Select.Option value="SAT">Saturday</Select.Option>
                  <Select.Option value="SUN">Sunday</Select.Option>
                </Select>
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      <Drawer
        title="Campaign Report"
        placement="right"
        onClose={() => setReportDrawerOpen(false)}
        open={reportDrawerOpen}
        width={600}
        closable={false}
        extra={
          <Button type="text" onClick={() => setReportDrawerOpen(false)}>
            x Close
          </Button>
        }
        footer={
          <Button
            size="large"
            type="primary"
            block
            onClick={() => setReportDrawerOpen(false)}
          >
            Close
          </Button>
        }
      >
        {report ? (
          <div className="space-y-6">
            <div>
              <h4 className="font-semibold mb-3">Overall Progress</h4>
              <Card size="small" className="bg-gray-50">
                <Row gutter={16}>
                  <Col span={12}>
                    <Statistic
                      title="Total Recipients"
                      value={report.stats?.totalRecipients}
                      formatter={(value) => value?.toLocaleString()}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Sent"
                      value={report.stats?.sentCount}
                      formatter={(value) => value?.toLocaleString()}
                    />
                  </Col>
                </Row>
                <Row gutter={16} className="mt-4">
                  <Col span={12}>
                    <Statistic
                      title="Delivered"
                      value={report.stats?.deliveredCount}
                      formatter={(value) => value?.toLocaleString()}
                      valueStyle={{ color: "#52c41a" }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Failed"
                      value={report.stats?.failedCount}
                      formatter={(value) => value?.toLocaleString()}
                      valueStyle={{ color: "#f5222d" }}
                    />
                  </Col>
                </Row>
                <Divider style={{ margin: "12px 0" }} />
                <div>
                  <p className="text-xs text-gray-500 mb-1">Success Rate</p>
                  <Progress
                    percent={Math.round(report.stats?.successRate || 0)}
                    status="success"
                    size="small"
                  />
                </div>
              </Card>
            </div>

            <Card size="small">
              <p className="">
                <strong>Campaign:</strong> {report.campaign?.name}
              </p>
              <p className="">
                <strong>Status:</strong>{" "}
                <Tag color={getStatusColor(report.campaign?.status)}>
                  {report.campaign?.status}
                </Tag>
              </p>
            </Card>

            <div>
              <h4 className="font-semibold mb-3">Overall Statistics</h4>
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic
                    title="Total Recipients"
                    value={report.stats?.totalRecipients}
                    formatter={(value) => value?.toLocaleString()}
                    size="small"
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Sent"
                    value={report.stats?.sentCount}
                    formatter={(value) => value?.toLocaleString()}
                    size="small"
                  />
                </Col>
              </Row>
            </div>

            {report.stats?.perChannelStats && (
              <div>
                <h4 className="font-semibold mb-3">Per-Channel Statistics</h4>
                {Object.entries(report.stats.perChannelStats).map(
                  ([channel, data]) => (
                    <Card key={channel} size="small" className="mb-2">
                      <p className="font-semibold  mb-2">{channel}</p>
                      <Row gutter={8}>
                        <Col span={8}>
                          <Statistic
                            title="Sent"
                            value={data.sent}
                            size="small"
                          />
                        </Col>
                        <Col span={8}>
                          <Statistic
                            title="Delivered"
                            value={data.delivered}
                            size="small"
                            valueStyle={{ color: "#52c41a" }}
                          />
                        </Col>
                        <Col span={8}>
                          <Statistic
                            title="Failed"
                            value={data.failed}
                            size="small"
                            valueStyle={{ color: "#f5222d" }}
                          />
                        </Col>
                      </Row>
                    </Card>
                  ),
                )}
              </div>
            )}

            {report.timing && (
              <div>
                <h4 className="font-semibold mb-3">Timing</h4>
                <Card size="small">
                  <p className=" mb-2">
                    <strong>Started:</strong>{" "}
                    {new Date(report.timing.startedAt).toLocaleString()}
                  </p>
                  {report.timing.completedAt && (
                    <p className=" mb-2">
                      <strong>Completed:</strong>{" "}
                      {new Date(report.timing.completedAt).toLocaleString()}
                    </p>
                  )}
                  <p className="">
                    <strong>Duration:</strong>{" "}
                    {Math.round(report.timing.durationSeconds / 60)} minutes
                  </p>
                </Card>
              </div>
            )}

            {Array.isArray(campaignLogs) && campaignLogs.length > 0 && (
              <div className="mt-4">
                <h4 className="font-semibold mb-3">Activity & Link Click Logs</h4>
                <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                  {campaignLogs.map((log) => (
                    <Card key={log.id} size="small" className="bg-gray-50 border-gray-200 shadow-none">
                      <div className="flex justify-between items-start text-xs">
                        <div className="flex-1">
                          <span
                            className={`inline-block font-gilroyBold px-2 py-0.5 rounded text-[11px] mb-1 ${
                              log.action === "MAGIC_LINK_CLICKED"
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-200 text-gray-800"
                            }`}
                          >
                            {log.action}
                          </span>
                          <p className="text-gray-700 text-xs mb-0 leading-snug">{log.details}</p>
                        </div>
                        <span className="text-gray-400 font-mono text-[11px] flex-shrink-0 ml-2">
                          {new Date(log.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <p>No report available</p>
        )}
      </Drawer>

      <Modal
        title="Preview Campaign"
        open={previewModalOpen}
        onCancel={() => setPreviewModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setPreviewModalOpen(false)}>
            Close
          </Button>,
        ]}
        centered
        width={600}
      >
        <Spin spinning={loading} indicator={antIcon}>
          <div className="space-y-4">
            {getRequiredVariables().size > 0 ? (
              <>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {AVAILABLE_VARIABLES.filter((v) =>
                    getRequiredVariables().has(v.name),
                  ).map((variable) => (
                    <div key={variable.name}>
                      <label className="block font-medium mb-2 text-sm">
                        {variable.name}
                      </label>
                      <Input
                        size="large"
                        placeholder={variable.example}
                        value={sampleData[variable.name] || ""}
                        onChange={(e) =>
                          setSampleData({
                            ...sampleData,
                            [variable.name]: e.target.value,
                          })
                        }
                      />
                    </div>
                  ))}
                </div>

                <Button
                  onClick={handlePreview}
                  loading={loading}
                  type="primary"
                  size="large"
                  block
                >
                  Generate Preview
                </Button>
              </>
            ) : (
              <p className="text-gray-500 text-center py-4">
                No variables used in template
              </p>
            )}

            {templatePreview && (
              <Card className="bg-blue-50 border border-blue-200 mt-4">
                <div className="space-y-4">
                  <div>
                    <p className="font-bold mb-3 text-base text-gray-800">
                      Title:
                    </p>
                    <p className="text-blue-900 font-medium text-lg p-3 bg-white rounded border border-blue-100">
                      {templatePreview.title}
                    </p>
                  </div>
                  <Divider style={{ margin: "12px 0" }} />
                  <div>
                    <p className="font-bold mb-3 text-base text-gray-800">
                      Body:
                    </p>
                    <div
                      className="text-blue-900 p-4 bg-white rounded border border-blue-100 max-h-64 overflow-y-auto"
                      style={{
                        lineHeight: "1.8",
                        fontSize: "15px",
                        whiteSpace: "pre-wrap",
                        wordWrap: "break-word",
                      }}
                    >
                      <div
                        dangerouslySetInnerHTML={{
                          __html: templatePreview.body,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </Spin>
      </Modal>

      <Modal
        title="Available Variables"
        open={variablesModalOpen}
        onCancel={() => setVariablesModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setVariablesModalOpen(false)}>
            Close
          </Button>,
        ]}
        centered
        width={500}
      >
        <div className="space-y-3">
          {AVAILABLE_VARIABLES.map((variable) => (
            <div
              key={variable.name}
              className="bg-gray-50 p-3 rounded flex justify-between items-center"
            >
              <div className="flex-1">
                <p className="font-mono text-blue-600 font-medium">{`{{${variable.name}}}`}</p>
                <p className="text-xs text-gray-600 mt-1">
                  {variable.description}
                </p>
              </div>
              <Button
                type="text"
                size="small"
                icon={<CopyIcon />}
                onClick={() => copyVariable(variable.name)}
              >
                Copy
              </Button>
            </div>
          ))}
        </div>
      </Modal>
    </Spin>
  );
};

export default AdminCampaigns;
